import axios from 'axios';
import {saveAs} from 'file-saver';
import {
  AdresseSuggestion,
  ApsRequestPayload,
  DivopsApsHistoryFilters,
  DivopsApsHistoryQueryParams,
  DivopsApsRequestEntry,
  CommandePayload,
  CommandeResult,
  Contact,
  DivopsAssistancePayload,
  DivopsHistoryFilters,
  DivopsHistoryQueryParams,
  DivopsSearchHistoryEntry,
  EligibiliteMasseSubmission,
  EligibilityFormValues,
  EligibilityResult,
  GpsCoordinates,
  PorteCollecte,
  SiretLookupResult,
} from '../Types/types';
import {ApiResponse} from '../../../types/types';
import {TicketCreationResponse} from '../../Ticket/Api/forms';
import {getSystem} from '../../../app/pages/dashboard/api';

export const geocodeAdresse = async (query: string): Promise<GpsCoordinates | null> => {
  // fetch (et non axios) pour ne pas envoyer les en-têtes d'authentification de l'app à un service tiers
  const params = new URLSearchParams({format: 'json', limit: '1', countrycodes: 'fr', q: query});
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
  if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);
  const data: {lat: string; lon: string}[] = await response.json();
  if (data.length === 0) return null;
  return {latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon)};
};

export const reverseGeocodeAdresse = async (coordinates: GpsCoordinates): Promise<string | null> => {
  const params = new URLSearchParams({
    format: 'json',
    lat: String(coordinates.latitude),
    lon: String(coordinates.longitude),
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`);
  if (!response.ok) throw new Error(`Reverse geocoding failed: ${response.status}`);
  const data: {display_name?: string} = await response.json();
  return data.display_name ?? null;
};

export const getAdresseSuggestions = async (query: string): Promise<AdresseSuggestion[]> => {
  const {data} = await axios.get<AdresseSuggestion[]>('/api/divops/adresses', {
    params: {query},
  });
  return data;
};

export const getAdressesBySiret = async (siret: string): Promise<SiretLookupResult> => {
  const {data} = await axios.get<SiretLookupResult>(`/api/divops/adresses/by-siret/${siret}`);
  return data;
};

export const checkEligibility = async (form: EligibilityFormValues): Promise<EligibilityResult> => {
  const {data} = await axios.post<EligibilityResult>('/api/divops/eligibilite', {
    codePostal: form.codePostal,
    codeInsee: form.codeInsee,
    adresse: form.adresse,
    latitude: form.latitude,
    longitude: form.longitude,
  });
  return data;
};

export const createCommande = async (payload: CommandePayload): Promise<CommandeResult> => {
  const {data} = await axios.post<CommandeResult>('/api/divops/commande', payload);
  return data;
};

export const getContacts = async (): Promise<Contact[]> => {
  const {data} = await axios.get<Contact[]>('/api/divops/contacts');
  return data;
};

export const getPortesCollecte = async (): Promise<PorteCollecte[]> => {
  const {data} = await axios.get<PorteCollecte[]>('/api/divops/portes-collecte');
  return data;
};

export const getSearchHistory = async (
  params: DivopsHistoryQueryParams
): Promise<ApiResponse<DivopsSearchHistoryEntry[]>> => {
  const {data} = await axios.get<ApiResponse<DivopsSearchHistoryEntry[]>>(
    '/api/divops/historique',
    {params}
  );
  return data;
};

export const getSearchHistoryFilters = async (): Promise<DivopsHistoryFilters> => {
  const {data} = await axios.get<DivopsHistoryFilters>('/api/divops/historique/filtres');
  return data;
};

const APS_FORM_KEY = 'aps_divop';

const buildApsDescription = (payload: ApsRequestPayload): string =>
  [
    `Service souhaité : ${payload.serviceSouhaite}`,
    payload.adresse ? `Adresse : ${payload.adresse}` : null,
    payload.nombreBrins !== undefined ? `Nombre de brins : ${payload.nombreBrins}` : null,
    `SPOF : ${payload.spof === 'oui' ? 'Oui' : 'Non'}`,
    payload.optionsSecurisation.length > 0
      ? `Options de sécurisation : ${payload.optionsSecurisation.join(', ')}`
      : null,
    payload.budget ? `Budget du client : ${payload.budget} EUR` : null,
    payload.delai ? `Délai de mise en service souhaité : ${payload.delai}` : null,
    `Durée : ${payload.duree}`,
    `Type : ${payload.type}`,
    payload.informationComplementaire
      ? `Information complémentaire :\n${payload.informationComplementaire}`
      : null,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');

export const submitApsRequest = async (
  payload: ApsRequestPayload
): Promise<TicketCreationResponse> => {
  const formData = new FormData();
  formData.append('form', APS_FORM_KEY);
  if (payload.customerId != null) {
    formData.append('customerId', payload.customerId.toString());
  }
  formData.append('datas[origine]', getSystem(payload.clientId));
  formData.append('datas[typeDemande]', `APS - ${payload.buName} - ${payload.serviceSouhaite}`);
  formData.append('datas[descriptif]', buildApsDescription(payload));
  if (payload.file) {
    formData.append('file', payload.file);
  }

  const {data} = await axios.post<TicketCreationResponse>('/api/reclamation/send', formData);

  // L'historique est secondaire : un échec d'enregistrement ne doit pas faire échouer la demande.
  try {
    await saveApsRequest(payload, data?.TicketNumber);
  } catch {
    // ignoré volontairement, le ticket OTRS est déjà créé
  }

  return data;
};

const saveApsRequest = async (
  payload: ApsRequestPayload,
  ticketNumber?: string | number
): Promise<void> => {
  await axios.post('/api/divops/aps-demandes', {
    customerId: payload.customerId,
    adresse: payload.adresse,
    serviceSouhaite: payload.serviceSouhaite,
    nombreBrins: payload.nombreBrins,
    spof: payload.spof,
    optionsSecurisation: payload.optionsSecurisation,
    budget: payload.budget !== undefined ? String(payload.budget) : undefined,
    delai: payload.delai,
    duree: payload.duree,
    type: payload.type,
    informationComplementaire: payload.informationComplementaire,
    ticketNumber: ticketNumber ? String(ticketNumber) : undefined,
  });
};

export const getApsRequests = async (
  params: DivopsApsHistoryQueryParams
): Promise<ApiResponse<DivopsApsRequestEntry[]>> => {
  const {data} = await axios.get<ApiResponse<DivopsApsRequestEntry[]>>('/api/divops/aps-demandes', {
    params,
  });
  return data;
};

export const getApsRequestsFilters = async (): Promise<DivopsApsHistoryFilters> => {
  const {data} = await axios.get<DivopsApsHistoryFilters>('/api/divops/aps-demandes/filtres');
  return data;
};

export const sendDivopsAssistance = async (
  payload: DivopsAssistancePayload
): Promise<{message: string}> => {
  const {data} = await axios.post<{message: string}>('/api/divops/assistance', payload);
  return data;
};

export const uploadEligibiliteMasseFile = async (
  file: File
): Promise<EligibiliteMasseSubmission> => {
  const formData = new FormData();
  formData.append('file', file);
  const {data} = await axios.post<EligibiliteMasseSubmission>(
    '/api/divops/eligibilite-masse',
    formData
  );
  return data;
};

export const getEligibiliteMasseSubmissions = async (): Promise<EligibiliteMasseSubmission[]> => {
  const {data} = await axios.get<EligibiliteMasseSubmission[]>('/api/divops/eligibilite-masse');
  return data;
};

export const downloadEligibiliteMasseResult = async (id: number): Promise<void> => {
  const response = await axios.get(`/api/divops/eligibilite-masse/${id}/download`, {
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] as string | undefined;
  const filename = disposition?.split('filename=')[1]?.split(';')[0] ?? `MasseElig_${id}.csv`;
  saveAs(new Blob([response.data], {type: 'text/csv;charset=utf-8;'}), filename);
};

export const downloadEligibiliteMasseTemplate = (): void => {
  const lines = [
    'NOM SITE;ADRESSE POSTALE;SUPPORT;LATITUDE;LONGITUDE;DEBIT;TECHNOLOGIE',
    'MAIRIE;1 Rue de la Mairie, 75001 Paris;0100000000;;;100M;fibre',
  ];
  const blob = new Blob([lines.join('\n')], {type: 'text/csv;charset=utf-8;'});
  saveAs(blob, 'modele_eligibilite_masse.csv');
};
